<script setup lang="ts">
import { NegotiationState } from '~/apiClient';
import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
});

// MODEL
const isModalOpen = defineModel<boolean>({ required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt } = useTypedI18n();
const { negotiation, sellerDecline, sellerDeclineIsPending } = useAuctionNegotiation(() => auction);

// METHODS
const closeModal = (): void => {
    isModalOpen.value = false;
};

const onConfirm = async (): Promise<void> => {
    try {
        await sellerDecline();
        closeModal();
    } catch {}
};

// WATCHERS
watch(
    () => negotiation.value.state,
    value => isModalOpen.value && value !== NegotiationState.Open && closeModal()
);
</script>

<template>
    <UModal
        v-model:open="isModalOpen"
        :fullscreen="isMobile"
        :title="tt('auctionNegotiation.sellerDeclineModal.title')"
        v-bind="modalNoDescAriaFixupProps"
    >
        <template #body>
            <p class="text-xs text-neutral-500 lg:text-sm">
                {{ tt('auctionNegotiation.sellerDeclineModal.disclaimer') }}
            </p>
        </template>
        <template #footer="{ close }">
            <UButton
                size="xl"
                color="error"
                :label="tt('auctionNegotiation.sellerDeclineModal.confirmButton')"
                block
                :loading="sellerDeclineIsPending"
                @click="onConfirm"
            />
            <UButton size="xl" color="neutral" variant="link" :label="tt('auctionNegotiation.cancel')" @click="close" />
        </template>
    </UModal>
</template>
